import React, { useState } from 'react';
import { Users, TrendingDown, AlertTriangle, CheckCircle, Search, Filter, Download, Calendar, Clock, Phone, Eye } from 'lucide-react';
import ReportPage from './ReportPage';

interface Employee {
  id: string;
  name: string;
  role: string;
  department: string;
  todayUsage: number;
  weeklyAverage: number;
  goalMinutes: number;
  status: 'excellent' | 'good' | 'warning' | 'critical';
  lastActive: string;
  trend: 'up' | 'down' | 'stable';
}

export default function ManagerDashboard() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [showReport, setShowReport] = useState(false);

  const employees: Employee[] = [
    {
      id: '1',
      name: 'Sarah Johnson',
      role: 'Delivery Driver',
      department: 'Logistics',
      todayUsage: 15,
      weeklyAverage: 22,
      goalMinutes: 30,
      status: 'excellent',
      lastActive: '2 hours ago',
      trend: 'down'
    },
    {
      id: '2',
      name: 'Mike Chen',
      role: 'Warehouse Supervisor',
      department: 'Operations',
      todayUsage: 45,
      weeklyAverage: 38,
      goalMinutes: 30,
      status: 'warning',
      lastActive: '30 minutes ago',
      trend: 'up'
    },
    {
      id: '3',
      name: 'Emily Rodriguez',
      role: 'Safety Coordinator',
      department: 'Safety',
      todayUsage: 12,
      weeklyAverage: 18,
      goalMinutes: 30,
      status: 'excellent',
      lastActive: '1 hour ago',
      trend: 'down'
    },
    {
      id: '4',
      name: 'David Thompson',
      role: 'Delivery Driver',
      department: 'Logistics',
      todayUsage: 65,
      weeklyAverage: 72,
      goalMinutes: 30,
      status: 'critical',
      lastActive: '15 minutes ago',
      trend: 'down'
    },
    {
      id: '5',
      name: 'Lisa Park',
      role: 'Fleet Manager',
      department: 'Operations',
      todayUsage: 28,
      weeklyAverage: 25,
      goalMinutes: 30,
      status: 'good',
      lastActive: '45 minutes ago',
      trend: 'stable'
    },
    {
      id: '6',
      name: 'James Wilson',
      role: 'Delivery Driver',
      department: 'Logistics',
      todayUsage: 8,
      weeklyAverage: 14,
      goalMinutes: 30,
      status: 'excellent',
      lastActive: '3 hours ago',
      trend: 'down'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'good': return 'text-cyan-600 bg-cyan-50 border-cyan-200';
      case 'warning': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingDown className="h-4 w-4 text-red-500 rotate-180" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-emerald-500" />;
      default: return <div className="h-4 w-4 bg-gray-400 rounded-full" />;
    }
  };

  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = selectedDepartment === 'all' || employee.department === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  const totalEmployees = employees.length;
  const averageUsage = Math.round(employees.reduce((sum, emp) => sum + emp.todayUsage, 0) / totalEmployees);
  const complianceRate = Math.round((employees.filter(emp => emp.todayUsage <= emp.goalMinutes).length / totalEmployees) * 100);
  const criticalAlerts = employees.filter(emp => emp.status === 'critical').length;

  if (showReport) {
    return <ReportPage onBack={() => setShowReport(false)} />;
  }

  return (
    <div>
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-8">
          <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-2xl lg:rounded-3xl shadow-lg border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2">
              <Users className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-cyan-500 mb-1 sm:mb-0" />
              <span className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{totalEmployees}</span>
            </div>
            <p className="text-gray-600 text-xs sm:text-sm">Total Employees</p>
          </div>
          
          <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-2xl lg:rounded-3xl shadow-lg border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-emerald-500 mb-1 sm:mb-0" />
              <span className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{averageUsage}m</span>
            </div>
            <p className="text-gray-600 text-xs sm:text-sm">Avg Daily Usage</p>
          </div>
          
          <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-2xl lg:rounded-3xl shadow-lg border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2">
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-violet-500 mb-1 sm:mb-0" />
              <span className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{complianceRate}%</span>
            </div>
            <p className="text-gray-600 text-xs sm:text-sm">Compliance Rate</p>
          </div>
          
          <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-2xl lg:rounded-3xl shadow-lg border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2">
              <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-red-500 mb-1 sm:mb-0" />
              <span className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{criticalAlerts}</span>
            </div>
            <p className="text-gray-600 text-xs sm:text-sm">Critical Alerts</p>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-2xl lg:rounded-3xl shadow-lg border border-gray-100 p-4 sm:p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex items-center space-x-4 w-full md:w-auto">
              <div className="relative flex-1 md:w-64 lg:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
              
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              >
                <option value="all">All Departments</option>
                <option value="Logistics">Logistics</option>
                <option value="Operations">Operations</option>
                <option value="Safety">Safety</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setShowReport(true)}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base bg-cyan-500 text-white rounded-xl sm:rounded-2xl hover:bg-cyan-600 transition-colors"
              >
                <Download className="h-4 w-4" />
                <span>Export Report</span>
              </button>
            </div>
          </div>
        </div>

        {/* Employee List */}
        <div className="bg-white rounded-2xl lg:rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Employee Phone Usage</h2>
          </div>
          
          {/* Mobile Card Layout */}
          <div className="block md:hidden">
            <div className="divide-y divide-gray-100">
              {filteredEmployees.map((employee) => (
                <div key={employee.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">{employee.name}</div>
                      <div className="text-xs text-gray-500">{employee.role} • {employee.department}</div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(employee.status)}`}>
                      {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        <Phone className="h-3 w-3 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{employee.todayUsage}m</span>
                      </div>
                      <div className="text-xs text-gray-500">Today</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-900 mb-1">{employee.weeklyAverage}m</div>
                      <div className="text-xs text-gray-500">Weekly Avg</div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        {getTrendIcon(employee.trend)}
                        <span className="text-sm font-medium text-gray-900">{employee.goalMinutes}m</span>
                      </div>
                      <div className="text-xs text-gray-500">Goal</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Last active: {employee.lastActive}
                    </div>
                    <button 
                      onClick={() => setSelectedEmployee(employee)}
                      className="flex items-center space-x-1 text-cyan-600 hover:text-cyan-700 transition-colors"
                    >
                      <Eye className="h-3 w-3" />
                      <span className="text-xs">Details</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Employee</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Today's Usage</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Weekly Avg</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Goal</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Trend</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Last Active</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredEmployees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{employee.name}</div>
                        <div className="text-sm text-gray-500">{employee.role} • {employee.department}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{employee.todayUsage}m</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{employee.weeklyAverage}m</td>
                    <td className="px-6 py-4 text-gray-600">{employee.goalMinutes}m</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(employee.status)}`}>
                        {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {getTrendIcon(employee.trend)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{employee.lastActive}</td>
                    <td className="px-6 py-4">
                      <button 
                        onClick={() => setSelectedEmployee(employee)}
                        className="flex items-center space-x-1 text-cyan-600 hover:text-cyan-700 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        <span className="text-sm">View Details</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Employee Detail Modal */}
        {selectedEmployee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{selectedEmployee.name}</h3>
                    <p className="text-gray-600">{selectedEmployee.role} • {selectedEmployee.department}</p>
                  </div>
                  <button 
                    onClick={() => setSelectedEmployee(null)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-gray-50 p-4 rounded-2xl">
                    <div className="text-2xl font-bold text-gray-900 mb-1">{selectedEmployee.todayUsage}m</div>
                    <div className="text-sm text-gray-600">Today's Usage</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-2xl">
                    <div className="text-2xl font-bold text-gray-900 mb-1">{selectedEmployee.weeklyAverage}m</div>
                    <div className="text-sm text-gray-600">Weekly Average</div>
                  </div>
                </div>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Usage Progress</h4>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full ${selectedEmployee.todayUsage <= selectedEmployee.goalMinutes ? 'bg-emerald-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min((selectedEmployee.todayUsage / selectedEmployee.goalMinutes) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mt-1">
                    <span>0m</span>
                    <span>Goal: {selectedEmployee.goalMinutes}m</span>
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <button className="flex-1 bg-cyan-500 text-white py-3 px-4 rounded-2xl hover:bg-cyan-600 transition-colors">
                    Send Reminder
                  </button>
                  <button className="flex-1 border border-gray-300 text-gray-700 py-3 px-4 rounded-2xl hover:bg-gray-50 transition-colors">
                    View Full Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}